import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import { categories } from "@/mocks/videos";
import { countByCategory } from "@/lib/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "只做精选",
    text: "每天从大量内容里筛出少量值得看的片段，标题写清楚内容，不靠夸张封面骗点击。",
    tone: "bg-primary-100 text-primary-700",
  },
  {
    icon: "ri-layout-grid-line",
    title: "分类清楚",
    text: "娱乐、生活、搞笑、音乐、美食、旅行六大分类，配合时长与热度筛选，减少选择成本。",
    tone: "bg-accent-100 text-accent-700",
  },
  {
    icon: "ri-play-circle-line",
    title: "播放干净",
    text: "原生 HTML5 播放器，没有遮挡画面的浮层、没有强制连播，声音和进度都由你决定。",
    tone: "bg-secondary-100 text-secondary-700",
  },
];

const steps = [
  {
    title: "挑一个分类",
    text: "从首页的分类入口或顶部导航进入对应分类页，浏览最新的视频卡片。",
  },
  {
    title: "筛选你想看的",
    text: "按最新或最热排序，并用时长条件过滤，几十秒的碎片时间也能看完一条。",
  },
  {
    title: "播放与收藏",
    text: "详情页使用浏览器原生播放器，点收藏即可保存到本地，不需要注册账号。",
  },
  {
    title: "分享给朋友",
    text: "点击“分享链接”复制当前视频地址，直接粘贴到聊天窗口就能一起看。",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于茄子视频 - 优质短视频分享与观看平台",
    description:
      "了解茄子视频的定位、内容分类与使用方式。我们专注提供清晰分类、干净播放体验的短视频内容，并说明收藏数据的本地存储与隐私策略。",
    keywords: "关于茄子视频,短视频平台,视频分享平台,茄子视频介绍,隐私声明",
  });

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 pt-6 md:px-8 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于" }]} />

        <section className="mt-5 grid grid-cols-1 items-center gap-8 rounded-2xl bg-primary-50 p-6 md:p-10 lg:grid-cols-[1.15fr_0.85fr] lg:p-12">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              <span className="h-1 w-6 rounded-full bg-accent-500"></span>
              About Eggplant TV
            </p>
            <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[38px]">
              关于茄子视频
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              茄子视频是一个专注于优质短视频分享与在线播放的内容平台，面向喜欢娱乐、生活、搞笑内容的年轻用户与大众观众。我们把分类、封面、标题和播放体验做得尽量清楚，让你不用在杂乱的信息里反复滑动，也能找到下一条想看的内容。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-base"></i>
                去看今天的精选
              </Link>
              <a
                href="mailto:feedback@eggplant.tv"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-5 py-2.5 text-sm text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className="ri-mail-send-line text-base"></i>
                联系编辑部
              </a>
            </div>
            <p className="mt-5 font-label text-[11px] tracking-wide text-foreground-400">
              本站为演示站点，视频、作者与播放数据均为虚构内容。
            </p>
          </div>

          <div className="relative">
            <div className="aspect-[4/3] w-full overflow-hidden rounded-xl bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20three%20dimensional%20art%20composition%20with%20glossy%20eggplant%20purple%20spheres%2C%20soft%20orange%20arcs%20and%20rounded%20geometric%20shapes%20floating%20on%20a%20cream%20background%2C%20studio%20lighting%2C%20playful%20modern%20editorial%203d%20render%2C%20high%20detail%2C%20no%20text&width=900&height=680&seq=egg-about-visual&orientation=squarish"
                alt="茄子视频品牌视觉：紫色与橙色几何图形组合"
                title="茄子视频品牌视觉 - 优质短视频平台"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 hidden rounded-lg bg-background-50 px-4 py-3 sm:block">
              <p className="font-heading text-xl font-extrabold text-primary-600">2026</p>
              <p className="font-label text-[10px] uppercase tracking-[0.16em] text-foreground-500">
                Since · 每日更新
              </p>
            </div>
          </div>
        </section>

        <section className="mt-12 grid grid-cols-2 gap-3 md:mt-16 md:grid-cols-4 md:gap-4">
          {[
            { value: "6", label: "内容分类" },
            { value: "20+", label: "演示视频" },
            { value: "0", label: "注册门槛" },
            { value: "100%", label: "免费观看" },
          ].map((item) => (
            <div
              key={item.label}
              className="flex flex-col gap-1 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5"
            >
              <span className="font-heading text-2xl font-extrabold text-primary-600 md:text-3xl">
                {item.value}
              </span>
              <span className="font-label text-[11px] uppercase tracking-[0.14em] text-foreground-500">
                {item.label}
              </span>
            </div>
          ))}
        </section>

        <section className="mt-14 md:mt-20">
          <SectionHeading
            eyebrow="Our approach"
            title="我们在坚持什么"
            description="内容平台最基本的诚意，是让用户知道自己在点什么、看什么。"
          />
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {values.map((value) => (
              <div
                key={value.title}
                className="rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-200 md:p-6"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-lg ${value.tone}`}>
                  <i className={`${value.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{value.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-500">{value.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 md:mt-20">
          <SectionHeading
            eyebrow="Content map"
            title="内容分类一览"
            description="每个分类都有独立列表页，带排序、时长筛选和分页加载。"
            action={
              <Link
                to="/category/entertainment"
                className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                进入分类页
              </Link>
            }
          />
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                className="group flex cursor-pointer flex-col rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200 hover:bg-primary-50/50 md:p-5"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${category.icon} text-lg`}></i>
                  </span>
                  <span className="font-label text-[11px] text-foreground-400">
                    {countByCategory(category.id)} 条
                  </span>
                </div>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                  {category.name}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-500">
                  {category.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 md:mt-20">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-2xl bg-secondary-100 p-6 md:p-8">
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-secondary-700">
                How to use
              </p>
              <h2 className="mt-2 font-heading text-xl font-extrabold text-secondary-950 md:text-2xl">
                使用说明
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-secondary-800">
                茄子视频不需要登录，所有浏览、筛选、收藏与分享操作都可以直接完成。
              </p>
              <ol className="mt-6 flex flex-col gap-4">
                {steps.map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-800 font-label text-xs font-bold text-background-50">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-secondary-950">{step.title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-secondary-800">{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex flex-col gap-5">
              <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-keyhole-line text-lg text-primary-500"></i>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">隐私声明</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  本站不收集账号信息，不要求登录，也不使用第三方广告追踪。唯一的本地数据是“收藏列表”，它通过浏览器 localStorage 保存在你的设备上，不会上传到服务器。清除浏览器数据或更换设备后，收藏记录会随之消失。
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  搜索关键词只在当前页面内用于筛选演示数据，不会被记录或分享。
                </p>
              </div>

              <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
                <div className="flex items-center gap-2">
                  <i className="ri-information-line text-lg text-accent-600"></i>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">内容与联系</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  本站展示的视频标题、简介、作者与播放量均为虚构演示数据，视频文件来自公开示例素材，仅用于界面与播放器功能展示，不代表真实节目或人物。
                </p>
                <div className="mt-5 flex flex-col gap-2">
                  <a
                    href="mailto:feedback@eggplant.tv"
                    className="flex items-center justify-between rounded-md bg-background-100 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-mail-line text-base text-primary-500"></i>
                      内容反馈
                    </span>
                    <span className="font-label text-xs text-foreground-500">feedback@eggplant.tv</span>
                  </a>
                  <a
                    href="mailto:business@eggplant.tv"
                    className="flex items-center justify-between rounded-md bg-background-100 px-3.5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                  >
                    <span className="flex items-center gap-2">
                      <i className="ri-briefcase-4-line text-base text-primary-500"></i>
                      商务合作
                    </span>
                    <span className="font-label text-xs text-foreground-500">business@eggplant.tv</span>
                  </a>
                </div>
                <p className="mt-4 font-label text-[11px] tracking-wide text-foreground-400">
                  工作时间：每日 09:00 - 22:00，通常 1 个工作日内回复。
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}